import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './FeatureSection.css';

/**
 * Reusable feature section component
 * Used to display workstyle, process, and other feature information with image/video, description, and tags
 */
const FeatureSection = ({
  image,
  video,
  imageAlt,
  label,
  sublabel,
  title,
  description,
  buttonText,
  buttonLink,
  tags = [],
  animationEnabled = true,
  rotateTagIcons = false
}) => {
  const videoRef = useRef(null);

  // Control video playback based on animation toggle
  useEffect(() => {
    if (videoRef.current && video) {
      if (animationEnabled) {
        videoRef.current.play().catch(err => {
          console.log('Video play failed:', err);
        });
      } else {
        videoRef.current.pause();
      }
    }
  }, [animationEnabled, video]);

  return (
    <section className="feature-section">
      <div className="feature-container">
        <div className="feature-image">
          {video ? (
            <video 
              ref={videoRef}
              autoPlay 
              loop 
              muted 
              playsInline
              className="feature-video"
            >
              <source src={video} type="video/mp4" />
            </video>
          ) : (
            <img src={image} alt={imageAlt} loading="lazy" />
          )}
        </div>
        <div className="feature-content">
          <span className="feature-label">{label}</span>
          <span className="feature-sublabel">{sublabel}</span>
          <h3 className="feature-title">{title}</h3>
          <p className="feature-desc">
            {description && description.split('\n').map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < description.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
          <Link to={buttonLink} className="feature-button">
            <span>{buttonText}</span>
            <span>
              <svg viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill="currentColor" d="M5.845 2.22a.75.75 0 0 1 1.06 0L9.23 4.543c.41.41.41 1.075 0 1.485L6.905 8.352a.75.75 0 0 1-1.06-1.061l1.25-1.251H.75a.75.75 0 0 1 0-1.5h6.354l-1.26-1.26a.75.75 0 0 1 0-1.06Z"/>
              </svg>
            </span>
          </Link>
          <div className="feature-tags">
            {tags.map((tag, index) => (
              <Link key={index} to={tag.link} className="feature-tag">
                <span className={`feature-tag-icon ${rotateTagIcons ? 'rotated' : ''}`}>
                  <svg viewBox="0 0 10 10" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5.845 2.22a.75.75 0 0 1 1.06 0L9.23 4.543c.41.41.41 1.075 0 1.485L6.905 8.352a.75.75 0 0 1-1.06-1.061l1.25-1.251H.75a.75.75 0 0 1 0-1.5h6.354l-1.26-1.26a.75.75 0 0 1 0-1.06Z"/>
                  </svg>
                </span>
                <span>{tag.text}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureSection;
